
import { useState, useEffect } from 'react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { LANGUAGE_COOKIE_NAME, defaultLanguage } from "@/lib/i18n";

export type SupportedLanguage = 'en' | 'de' | 'fr' | 'es';

type LanguageSelectorProps = {
  language: SupportedLanguage;
  onChange: (value: SupportedLanguage) => void;
  className?: string;
};

export function LanguageSelector({ language, onChange, className = "" }: LanguageSelectorProps) {
  const handleChange = (value: string) => {
    // Validate and convert to supported language
    const newLanguage = validateLanguage(value);
    onChange(newLanguage);
    localStorage.setItem(LANGUAGE_COOKIE_NAME, newLanguage);
    // Notify same-tab listeners (the `storage` event only fires in OTHER tabs).
    // Components like PolicyUpdateBanner subscribe to this to re-render copy.
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('paperverse:language-change', { detail: newLanguage }));
    }
  };

  return (
    <Select value={language} onValueChange={handleChange}>
      <SelectTrigger className={`w-[120px] ${className}`}>
        <SelectValue placeholder="Language" />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="en">English</SelectItem>
        <SelectItem value="de">Deutsch</SelectItem>
        <SelectItem value="fr">Français</SelectItem>
        <SelectItem value="es">Español</SelectItem>
      </SelectContent>
    </Select>
  );
}

// Helper function to validate language
function validateLanguage(lang: string): SupportedLanguage {
  if (['en', 'de', 'fr', 'es'].includes(lang)) {
    return lang as SupportedLanguage;
  }
  return defaultLanguage;
}

export default LanguageSelector;
